<!-- eslint-disable vue/no-v-html -->
<template>
  <article class="product-tile">
    <NuxtLink class="mobile-click-area" :to="linkToPdp" @click="emit('navigation-click')" />
    <NuxtLink v-if="linkToPdp" class="product-tile__image-link" :to="linkToPdp" @click="emit('navigation-click')">
      <CImage
        class="product-tile__image"
        :alt="product.name"
        :src="tileImage"
        :height="imageSize"
        :width="imageSize"
        :params="{ w: imageSize, h: imageSize, fit: 'contain' }"
      />
    </NuxtLink>

    <CImage
      v-else
      class="product-tile__image"
      :alt="product.name"
      :src="product.images"
      :height="imageSize"
      :width="imageSize"
      :params="{ w: imageSize, h: imageSize, fit: 'contain' }"
    />
    <h2 class="product-tile__heading">
      <NuxtLink v-if="linkToPdp" class="product-tile__heading-link" :to="linkToPdp" @click="emit('navigation-click')">
        {{ product.name }}
      </NuxtLink>
      <template v-else>
        {{ product.name }}
      </template>
    </h2>
    <div class="product-tile__description">
      <div
        ref="description"
        :class="{ 'product-tile__description--cropped': !isReadmore }"
        v-html="product.summary || product.text"
      />

      <CButton v-if="isDescriptionCropped && !isReadmore" class="md:hidden" variant="plain" @click="readMore">
        Visa mer
      </CButton>
    </div>
    <ul class="product-tile__attributes">
      <li v-for="(attribute, index) in sortedAttributes" :key="index" class="attribute">
        <span class="attribute__name">{{ attribute.attributeLabel || attribute.attributeName }}</span>
        <span class="attribute__value">{{ getAttributeValue(attribute) }}</span>
      </li>
    </ul>

    <div v-if="isExpandButtonVisible" class="product-tile__action">
      <slot />

      <CIconButton :name="buttonName" variant="primary" size="medium" icon-classes="text-white" @click="toggleProduct">
        <span class="c-icon-button__text-left">{{ buttonText }}</span>
      </CIconButton>
    </div>
    <div v-if="brandImage && brandImage.assetUrl" class="product-tile__brand">
      <CImage
        :src="brandImage.assetUrl"
        :alt="brandImage.brand"
        :fallback-image-src="brandImage.assetFallbackUrl"
        height="40"
        width="112"
        :params="{ w: '112', h: '40', fit: 'inside' }"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { Product } from '@/types/products';
import { useProductStore } from '~/stores';

interface Attribute {
  attributeValue: string;
  attributeLabel?: string; // Todo: Check if this is used. If not, remove it otherwise add to storybook
  attributeName: string;
  unit?: string;
}

interface Props {
  product: Product;
  expanded?: boolean;
  isExpandButtonVisible?: boolean;
  imageSize?: number;
  linkToPdp?: string | object /* RouteLocationRaw */;
  alternativeButtonText?: string;
  useProductImage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  expanded: false,
  isExpandButtonVisible: true,
  imageSize: 200,
  useProductImage: false,
  linkToPdp: undefined,
  alternativeButtonText: undefined,
});

const emit = defineEmits<{
  (e: 'toggleProduct'): void;
  (e: 'navigation-click'): void;
}>();

const { mq } = useMediaQueries();

const { sortSequence } = storeToRefs(useProductStore());
const description = ref(null);
const isReadmore = ref(false);
const isDescriptionCropped = ref(false);

const isSmallScreen = computed(() => mq.value.xsmall || mq.value.small || mq.value.medium);

const tileImage = computed(() => {
  const { variants, images } = props.product;
  if (!images?.length && variants) {
    return variants[0].images;
  } else if (props.useProductImage) {
    return images;
  }
  if (variants && variants.length === 1) {
    return variants[0].images;
  }
  // Check if the product image is included in the variants images
  const included = variants?.some(
    (variant) => variant.images && variant.images.find((image) => image.assetUrl === images?.[0].assetUrl),
  );
  // If the product image was found in the variants images, use the product images. Otherwise use the first variants images
  return included ? images : variants?.[0].images;
});

const buttonText = computed(() => {
  if (props.alternativeButtonText) {
    return props.alternativeButtonText;
  }
  const amountText = props.product.variants.length + (props.product.variants.length === 1 ? ' variant' : ' varianter');
  return props.expanded ? 'Dölj ' + amountText : 'Visa ' + amountText;
});

const buttonName = computed(() => {
  if (isSmallScreen.value) {
    return '';
  } else {
    return props.expanded ? 'close' : 'expand_more';
  }
});

const brandImage = computed(() => {
  const variantBrands = props.product.variants?.[0]?.brands;
  return variantBrands?.length ? variantBrands[0] : null;
});

const readMore = () => {
  isReadmore.value = !isReadmore.value;
};

const getAttributeValue = (attribute: Attribute) => {
  return attribute.unit ? `${attribute.attributeValue} ${attribute.unit}` : attribute.attributeValue;
};

const sortedAttributes = computed(() => {
  let sortedCommonAttributes = props.product.commonVariantAttributes;

  if (sortSequence.value?.length > 0) {
    sortedCommonAttributes = sortAttributesWithSequencing(sortedCommonAttributes, sortSequence.value, 'attributeName');
  }

  return sortedCommonAttributes;
});

const toggleProduct = () => {
  emit('toggleProduct');
};

onMounted(() => {
  if (description.value) {
    isDescriptionCropped.value =
      (description.value as HTMLElement).scrollHeight > (description.value as HTMLElement).clientHeight;
  }
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductTile',
});
</script>

<style lang="scss" scoped>
$medium: 600px;
$large: 800px;
.product-tile {
  @apply bg-white grid rounded grid-cols-2 max-w-full p-6 min-h-48 gap-4;
  grid-template-areas: 'image brand' 'heading heading' 'description description' 'attributes attributes' 'action action';

  @container (min-width: #{$medium}) {
    @apply grid-cols-[120px_auto_90px] grid-rows-[28px_minmax(28px,_72px)_auto_1fr];
    grid-template-areas: 'image heading brand' 'image description brand' 'attributes attributes attributes' 'action action action';
  }

  @container (min-width: #{$large}) {
    @apply grid-cols-[120px_auto_200px] grid-rows-[28px_32px_auto_1fr];
    grid-template-areas: 'image heading brand' 'image description brand' '. attributes action' '. attributes action';
  }

  .mobile-click-area {
    @apply col-start-1 col-end-3 row-start-1 row-end-3;

    @container (min-width: #{$medium}) {
      @apply hidden;
    }
  }

  &__margin {
    margin-top: 1rem;
  }

  &__heading {
    @apply text-sm text-primary-900 font-semibold truncate;

    grid-area: heading;

    @container (min-width: #{$medium}) {
      @apply text-xl;
    }
  }

  &__heading-link {
    @apply hover:underline;
    outline-offset: -2px;
  }

  &__image {
    @apply inline-block object-contain aspect-square;
  }

  &__image,
  &__image-link {
    grid-area: image;
  }

  &__attributes {
    @apply block min-w-0 ml-0;
    grid-area: attributes;

    .attribute {
      @apply inline-block text-xs mb-0.5 px-2 border-l border-solid border-gray-200;

      &:first-child {
        @apply border-l-0 pl-0;
      }

      &__name {
        &:after {
          content: ': ';
        }
      }

      &__value {
        @apply font-bold;
      }
    }
  }

  &__description {
    @apply text-xs text-gray-800;
    grid-area: description;

    @container (min-width: #{$medium}) {
      @apply text-xs line-clamp-2;
      max-width: 480px;
    }

    &--cropped {
      @apply line-clamp-2;

      * {
        @apply inline;
      }
    }

    > .c-button {
      @apply p-0 text-xs font-bold underline;
    }
  }

  &__brand {
    @apply flex justify-end;
    grid-area: brand;

    :deep(img) {
      @apply mr-0 ml-auto h-auto;
    }
  }

  &__action {
    @apply flex flex-col justify-end gap-4 mt-2;
    grid-area: action;

    @container (min-width: #{$medium}) {
      @apply flex-row mt-0;
      .c-button {
        @apply max-w-min;
      }
    }

    @container (min-width: #{$large}) {
      @apply flex-col items-center;
      .c-button {
        @apply max-w-none;
      }
    }

    > .c-button {
      @apply w-full text-sm h-10;
    }
  }
}
</style>
